import React, { useEffect, useRef, useState } from "react";
import {
  GoogleMap,
  Marker,
  Circle,
  OverlayView,
  Polyline,
} from "@react-google-maps/api";
import { useDispatch, useSelector } from "react-redux";
import { AppDispatch, RootState } from "../../app/store";
import { fetchSubmitSurveyById } from "../../app/surveySlice";
import { FileText, X } from "lucide-react";
import { Spin } from "antd";

interface Submission {
  lat: string;
  long: string;
  user_first_name: string;
  user_last_name: string;
  submitted_at: string;
  survey_submitted?: boolean;
  survey_title?: string;
  zone_alert?: number;
  submission_id?: number;
}

interface ProjectMapProps {
  startLat: number;
  startLng: number;
  endLat?: number | null;
  endLng?: number | null;
  startLocation: string;
  endLocation?: string;
  submissions: Submission[];
}

const containerStyle = {
  width: "100%",
  height: "600px",
};

const getZoneColor = (zone_alert?: number) => {
  switch (zone_alert) {
    case 1:
      return "#FF0000"; // High
    case 2:
      return "#FFA500"; // Medium
    case 3:
      return "#00FF00"; // Low
    default:
      return "#808080"; // Unknown
  }
};

const MultipleRoutesMap: React.FC<ProjectMapProps> = ({
  startLat,
  startLng,
  endLat,
  endLng,
  startLocation,
  endLocation,
  submissions,
}) => {
  const dispatch = useDispatch<AppDispatch>();
  const { submitSurveyData, surveyDetailsLoading } = useSelector(
    (state: RootState) => state.survey,
  );
  const mapRef = useRef<google.maps.Map | null>(null);
  const [activeCircleIdx, setActiveCircleIdx] = useState<number | null>(null);
  const [activeMarker, setActiveMarker] = useState<"start" | "end" | null>(
    null,
  );
  const iframeRef = useRef<HTMLIFrameElement | null>(null);
  const [selectedSubmission, setSelectedSubmission] =
    useState<Submission | null>(null);
  const openPdfInNewTab = () => {
    if (!submitSurveyData?.pdf) return;
    window.open(submitSurveyData.pdf, "_blank");
  };

  const handleCircleClick = (submission: Submission) => {
    setSelectedSubmission(submission);
    if (submission.submission_id) {
      // Clear previous data and fetch fresh data
      dispatch(fetchSubmitSurveyById(submission.submission_id));
    }
  };

  const routeFrom = { lat: startLat, lng: startLng };
  const hasValidEnd =
    typeof endLat === "number" &&
    typeof endLng === "number" &&
    !isNaN(endLat) &&
    !isNaN(endLng);

  const handleMapLoad = (map: google.maps.Map) => {
    mapRef.current = map;
  };

  // Adjust bounds on prop/submission change
  useEffect(() => {
    if (!mapRef.current) return;

    const bounds = new window.google.maps.LatLngBounds();
    bounds.extend(routeFrom);
    if (hasValidEnd) {
      bounds.extend({ lat: endLat as number, lng: endLng as number });
    }

    submissions.forEach((submission) => {
      const lat = parseFloat(submission.lat);
      const lng = parseFloat(submission.long);
      if (!isNaN(lat) && !isNaN(lng)) {
        bounds.extend({ lat, lng });
      }
    });

    mapRef.current.fitBounds(bounds, 80); // padding
  }, [startLat, startLng, endLat, endLng, submissions]);

  const mapOptions: google.maps.MapOptions = {
    mapTypeId: "terrain",
    styles: [
      // Hide all labels first
      {
        featureType: "all",
        elementType: "labels",
        stylers: [{ visibility: "off" }],
      },

      // Show city names
      {
        featureType: "administrative.locality",
        elementType: "labels.text",
        stylers: [{ visibility: "on" }],
      },

      // Show admin labels (this includes states)
      {
        featureType: "administrative",
        elementType: "labels.text",
        stylers: [{ visibility: "on" }],
      },

      // Hide country name
      {
        featureType: "administrative.country",
        elementType: "labels.text",
        stylers: [{ visibility: "off" }],
      },

      // Hide roads
      {
        featureType: "road",
        elementType: "labels",
        stylers: [{ visibility: "off" }],
      },

      // Hide POI
      {
        featureType: "poi",
        elementType: "labels",
        stylers: [{ visibility: "off" }],
      },
    ],
  };

  return (
    <>
      <GoogleMap
        mapContainerStyle={containerStyle}
        onLoad={handleMapLoad}
        mapTypeId={"terrain"} // fallback if google is undefined initially
        //center={routeFrom}
        options={mapOptions}
      >
        {/* Start Marker */}
        <Marker
          position={routeFrom}
          onMouseOver={() => setActiveMarker("start")}
          onMouseOut={() => setActiveMarker(null)}
        />
        {/* Start Marker Tooltip */}
        {activeMarker === "start" && startLocation && (
          <OverlayView
            position={routeFrom}
            mapPaneName={OverlayView.OVERLAY_MOUSE_TARGET}
          >
            <div
              onMouseEnter={() => setActiveMarker("start")}
              onMouseLeave={() => setActiveMarker(null)}
              className="relative z-[100]"
            >
              <div className="w-[270px] bg-[#1e293b] text-white px-4 py-3 rounded-xl shadow-xl text-sm transform -translate-x-1/2 -translate-y-[130%] border border-gray-600">
                <div className="mb-2 font-medium">Casualty Location</div>
                <div>{startLocation}</div>
                <div className="absolute left-1/2 top-full -translate-x-1/2 border-x-8 border-x-transparent border-t-[8px] border-[#1e293b]"></div>
              </div>
            </div>
          </OverlayView>
        )}

        {/* Submission Circles */}
        {submissions.map((submission, idx) => {
          const lat = parseFloat(submission.lat);
          const lng = parseFloat(submission.long);
          if (isNaN(lat) || isNaN(lng)) return null;

          const fullName = `${submission.user_first_name} ${submission.user_last_name}`;
          const zoneColor = getZoneColor(submission.zone_alert);

          return (
            <React.Fragment key={idx}>
              <Circle
                center={{ lat, lng }}
                radius={16}
                options={{
                  fillColor: zoneColor,
                  fillOpacity: 0.8,
                  strokeColor: zoneColor,
                  strokeOpacity: 1,
                  strokeWeight: 15,
                  zIndex: 20,
                }}
                onClick={() => handleCircleClick(submission)}
              />
            </React.Fragment>
          );
        })}

        {/* Legend */}
        <OverlayView
          position={{ lat: startLat - 0.005, lng: startLng + 0.01 }}
          mapPaneName={OverlayView.OVERLAY_MOUSE_TARGET}
        >
          <div className="bg-white p-3 rounded-lg shadow-lg text-xs w-[170px]">
            <div className="font-semibold mb-2">🧭 Zone Alert Legend</div>
            <div className="flex items-center mb-1">
              <div className="w-4 h-1 bg-red-500 mr-2"></div>
              <span>🔴 High Risk</span>
            </div>
            <div className="flex items-center mb-1">
              <div className="w-4 h-1 bg-orange-500 mr-2"></div>
              <span>🟠 Medium Risk</span>
            </div>
            <div className="flex items-center mb-1">
              <div className="w-4 h-1 bg-green-500 mr-2"></div>
              <span>🟢 Low Risk</span>
            </div>
            <div className="flex items-center">
              <div className="w-4 h-1 bg-gray-500 mr-2"></div>
              <span>Unknown</span>
            </div>
          </div>
        </OverlayView>
      </GoogleMap>

      {/* Sidebar Form */}
      {selectedSubmission && (
        <div className="fixed top-0 right-0 h-full w-[400px] bg-white shadow-2xl z-[999] border-l flex flex-col">
          <div className="flex justify-between items-center p-4 border-b bg-gray-50 flex-shrink-0">
            <h2 className="text-lg font-semibold">Survey Form Data</h2>
            <div className="flex gap-2">
              <button
                onClick={openPdfInNewTab}
                className="text-gray-500 px-3 py-1 rounded text-sm flex items-center gap-1"
              >
                <FileText size={16} />
              </button>

              <button
                onClick={() => setSelectedSubmission(null)}
                className="text-gray-500  px-3 py-2 rounded text-sm flex items-center"
              >
                <X size={16} />
              </button>
            </div>
          </div>
          <div className="flex-1 overflow-y-auto p-4 space-y-4">
            {surveyDetailsLoading || !submitSurveyData ? (
              <div className="flex items-center justify-center h-full">
                <Spin size="large" />
              </div>
            ) : (
              submitSurveyData.data.map((field, index) => (
                <div key={index} className="space-y-2">
                  <label className="block text-sm font-medium text-gray-700">
                    {field.label}
                  </label>
                  {field.type === "text" || field.type === "date" ? (
                    <input
                      type="text"
                      value={field.value || ""}
                      readOnly
                      className="w-full p-2 border rounded bg-gray-50 text-sm"
                    />
                  ) : field.type === "zone" ? (
                    <div className="flex items-center gap-2 p-2 border rounded bg-gray-50">
                      <span
                        className="w-3 h-3 rounded-full"
                        style={{ backgroundColor: field.color || "#000" }}
                      ></span>
                      <span className="text-sm">{field.value || "--"}</span>
                    </div>
                  ) : field.type === "file" ? (
                    <div className="grid grid-cols-2 gap-2">
                      {field.options?.length > 0 ? (
                        field.options.map((file, optIdx) => {
                          const lat = file.latitude;
                          const lng = file.longitude;
                          const imageUrl = file.value;
                          const mapsLink =
                            lat && lng
                              ? `https://www.google.com/maps?q=${lat},${lng}`
                              : null;

                          return (
                            <div
                              key={optIdx}
                              className="border rounded p-2 bg-gray-50"
                            >
                              <img
                                src={imageUrl}
                                alt="Uploaded"
                                onClick={() => window.open(imageUrl, "_blank")}
                                className="w-full h-24 object-cover rounded-lg border border-gray-300 dark:border-gray-600 shadow cursor-pointer hover:scale-105 transition-transform"
                                onError={(e) => {
                                  e.currentTarget.style.display = "none";
                                  const nextDiv = e.currentTarget
                                    .nextElementSibling as HTMLElement;
                                  if (nextDiv) nextDiv.style.display = "block";
                                }}
                              />
                              {/* <div
                                style={{ display: "none" }}
                                className="text-blue-600 underline text-sm"
                              >
                                <a
                                  href={imageUrl}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                >
                                  View File
                                </a>
                              </div> */}
                              <div className="text-[12px] text-gray-700 space-y-1">
                                <div>
                                  <span className="font-semibold text-gray-500">
                                    Lat:
                                  </span>{" "}
                                  {lat || "--"}
                                </div>
                                <div>
                                  <span className="font-semibold text-gray-500">
                                    Lng:
                                  </span>{" "}
                                  {lng || "--"}
                                </div>
                                {mapsLink && (
                                  <a
                                    href={mapsLink}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="text-[12px] text-blue-600 underline"
                                  >
                                    View on Map
                                  </a>
                                )}
                              </div>
                            </div>
                          );
                        })
                      ) : (
                        <div className="p-2 border rounded bg-gray-50 text-sm text-gray-500">
                          No file uploaded
                        </div>
                      )}
                    </div>
                  ) : field.options.length > 0 ? (
                    <div className="space-y-1">
                      {field.options.map((opt, optIdx) => (
                        <input
                          key={optIdx}
                          type="text"
                          value={opt.value}
                          readOnly
                          className="w-full p-2 border rounded bg-gray-50 text-sm"
                        />
                      ))}
                    </div>
                  ) : (
                    <input
                      type="text"
                      value="--"
                      readOnly
                      className="w-full p-2 border rounded bg-gray-50 text-sm text-gray-500"
                    />
                  )}
                </div>
              ))
            )}
          </div>
        </div>
      )}
      <iframe
        ref={iframeRef}
        style={{ display: "none" }}
        title="survey-pdf-print"
      />
    </>
  );
};

export default MultipleRoutesMap;
